import {HttpClient, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {environment} from '~environments/environment';
import {Contact} from '../../models';

/**
 * CRUD service for the contact management / activity history.
 */
@Injectable()
export class ContactManagementService {
  public API_URL_CONTACTS: any = `${environment.envAPI}/contacts`;
  public API_URL_CUSTOMERS: any = `${environment.envAPI}/customers`;

  public editContact: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public castEditContact: any = this.editContact.asObservable();

  public contactList: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public castContactList: any = this.contactList.asObservable();

  public limitDisplayResults: number = 5;

  constructor(private http: HttpClient) {
  }

  /**
   * Get all contacts.
   */
  public getAll(): Observable<Array<Contact>> {
    return this.http.get<Array<Contact>>(this.API_URL_CONTACTS).pipe(
      map(res => this.createContactClass(res['hydra:member']))
    );
  }

  /**
   * Get contacts by customer id.
   */
  public getById(id: number, limit: boolean = false): Observable<Array<Contact>> {
    let params: HttpParams = new HttpParams({});
    if (limit) {
      const limitStr = String(this.limitDisplayResults);
      const pageStr = String(1);
      params = new HttpParams()
        .set('order[important]', 'desc')
        .set('order[updatedAt]', 'desc')
        .set('page', pageStr)
        .set('itemsPerPage', limitStr);
    }

    return this.http.get<Array<Contact>>(`${this.API_URL_CUSTOMERS}/${id}/contacts`, {params})
      .pipe(
        map(res => this.createContactClass(res['hydra:member']))
      );
  }

  /**
   * Get the endpoint for customer contacts.
   */
  public getAllCustomerContactsEndpoint(customerId: number): any {
    const api = `${this.API_URL_CUSTOMERS}/${customerId}/contacts`;

    const params: Array<any> = [
      {'order[important]': 'desc'},
      {'order[updatedAt]': 'desc'},
    ];

    return [api, params];
  }

  /**
   * Creates the contact class.
   */
  public createContactClass(contacts: Array<Contact>): Array<Contact> {
    return contacts.map((contact: Contact) => new Contact(contact));
  }

  /**
   * Create activity history.
   */
  public create(contact: Contact): Observable<Contact> {
    return this.http.post<Contact>(`${this.API_URL_CONTACTS}`, contact).pipe(
      map(response => new Contact(response))
    );
  }

  /**
   * Update activity history.
   */
  public update(contact: Contact): any {
    return this.http.patch(`${this.API_URL_CONTACTS}/${contact.id}`, contact).pipe(
      map(response => new Contact(response))
    );
  }

  /**
   * Delete activity history.
   */
  public delete(id: number): any {
    return this.http.delete(`${this.API_URL_CONTACTS}/${id}`);
  }

  /**
   * Adds values to the observer.
   */
  public announceContactEdit(contact: Contact): void {
    this.editContact.next(contact);
  }

  /**
   * Adds values to the observer.
   */
  public setContactList(contact: Array<Contact>): void {
    this.contactList.next(contact);
    this.sortContacts();
  }

  /**
   * Clears the contact list.
   */
  public clearContactList(): void {
    this.contactList.next(null);
  }

  /**
   * Clear observable value.
   */
  public clearContactEdited(): void {
    this.editContact.next(null);
  }

  /**
   * Adds values to the observer.
   */
  public sortContacts(): void {
    const sortedBy = {true: 0, false: 1};
    const array = this.contactList.getValue();
    let sortedArray = array.sort((a, b): any => a.updatedAt.date < b.updatedAt.date ? 1 : -1);
    sortedArray = array.sort((a, b): any => sortedBy[a.important] - sortedBy[b.important]);
    this.contactList.next(sortedArray);
  }

  /**
   * Update a certain entry in the contacts list
   */
  public updateContactList(contact: Contact): void {
    const array = this.contactList.getValue();
    const index = array.findIndex(el => el.id === contact.id);
    array[index] = contact;
    this.sortContacts();
  }

  /**
   * Add a new entry to the contact list
   */
  public addToContactList(contact: Contact): void {
    const array = this.contactList.getValue();
    array.push(contact);
    this.contactList.value.totalItems++;
    this.sortContacts();
  }
}
